/* eslint-disable @reatom/unit-naming-rule */
import { action, atom } from '@reatom/framework'
import BigNumber from 'bignumber.js'

import { POOL_PERIOD_MINUTES_MAP } from '@blade/blockchain-types'
import { reatomBigNumber } from '@root/shared/lib'

import type { PairMetrics } from '../model'
import type { Ctx } from '@reatom/framework'
import type { ServicesApi } from '@root/shared/api'

export const reatomPairStatsForPeriod = (
  dto: ServicesApi.PairDto,
  period: PairMetrics.MetricsSpan,
) => {
  const transactionsAtom = atom(dto.periodStats[period].txs)

  const makersAtom = atom(dto.periodStats[period].makers)

  const buysAtom = atom(dto.periodStats[period].buys)
  const sellsAtom = atom(dto.periodStats[period].sells)

  const buyersAtom = atom(dto.periodStats[period].buyers)
  const sellersAtom = atom(dto.periodStats[period].sellers)

  const buyVolumeUsdAtom = reatomBigNumber(
    dto.periodStats[period].buyVolumeInUsd,
  )
  const sellVolumeUsdAtom = reatomBigNumber(
    dto.periodStats[period].sellVolumeInUsd,
  )
  const volumeUsdAtom = reatomBigNumber(dto.periodStats[period].volumeInUsd)

  const startPriceAtom = reatomBigNumber(dto.periodStats[period].startPrice)

  const priceChangeAtom = reatomBigNumber(
    dto.periodStats[period].priceChangePercents,
  )
  const volumeChangePercentsAtom = reatomBigNumber(
    dto.periodStats[period].volumeChangePercents,
  )
  const netVolumeInUsdt = BigNumber(dto.periodStats[period].netVolumeInUsd)
  const netVolumeInUsdAtom = reatomBigNumber(netVolumeInUsdt)

  const holdersChangeAtom = reatomBigNumber(
    dto.periodStats[period].holdersChangePercents,
  )

  /**
   * The trades this window counted itself, newest last, so they can be taken
   * back out when they age past its edge.
   *
   * Only kept for windows short enough that a trade will actually age out
   * while somebody is watching. Over a day nothing ages out of a session, and
   * holding a day of trades per pool to discover that would be the expensive
   * way to learn it.
   */
  const counted: Array<{ at: number; isBuy: boolean; volumeUsd: BigNumber }> =
    []
  const windowMs =
    period === 'ALL'
      ? undefined
      : POOL_PERIOD_MINUTES_MAP[period] * 60 * 1000
  const tracksExpiry = windowMs !== undefined && windowMs <= 60 * 60 * 1000

  const updateFromDtoAction = action(
    (ctx, dto: ServicesApi.PairDto['periodStats']) => {
      transactionsAtom(ctx, dto[period].txs)
      makersAtom(ctx, dto[period].makers)

      buysAtom(ctx, dto[period].buys)
      sellsAtom(ctx, dto[period].sells)

      buyersAtom(ctx, dto[period].buyers)
      sellersAtom(ctx, dto[period].sellers)

      buyVolumeUsdAtom.set(ctx, dto[period].buyVolumeInUsd)
      sellVolumeUsdAtom.set(ctx, dto[period].sellVolumeInUsd)
      volumeUsdAtom.set(ctx, dto[period].volumeInUsd)

      const netVolumeInUsdt = BigNumber(dto[period].netVolumeInUsd)

      netVolumeInUsdAtom.set(ctx, netVolumeInUsdt)
      startPriceAtom.set(ctx, dto[period].startPrice)
      priceChangeAtom.set(ctx, dto[period].priceChangePercents)
      volumeChangePercentsAtom.set(ctx, dto[period].volumeChangePercents)
      holdersChangeAtom.set(ctx, dto[period].holdersChangePercents)

      // The server's figure is the truth; what we counted towards it is spent.
      counted.length = 0
    },
  )

  const drop = (ctx: Ctx, trade: { isBuy: boolean; volumeUsd: BigNumber }) => {
    transactionsAtom(ctx, Math.max(0, ctx.get(transactionsAtom) - 1))
    if (trade.isBuy) {
      buysAtom(ctx, Math.max(0, ctx.get(buysAtom) - 1))
      buyVolumeUsdAtom.set(ctx, ctx.get(buyVolumeUsdAtom).minus(trade.volumeUsd))
    } else {
      sellsAtom(ctx, Math.max(0, ctx.get(sellsAtom) - 1))
      sellVolumeUsdAtom.set(
        ctx,
        ctx.get(sellVolumeUsdAtom).minus(trade.volumeUsd),
      )
    }
    volumeUsdAtom.set(ctx, ctx.get(volumeUsdAtom).minus(trade.volumeUsd))
    netVolumeInUsdAtom.set(
      ctx,
      ctx
        .get(netVolumeInUsdAtom)
        .minus(trade.isBuy ? trade.volumeUsd : trade.volumeUsd.negated()),
    )
  }

  /**
   * One trade, added to the window as it arrives.
   *
   * The figures otherwise only move when the server has rebuilt the pool's
   * whole card out of ClickHouse, which it does on a poll — about every three
   * seconds at best, and only for pools that traded. That is why a card could
   * sit unchanged for twenty seconds while the market under it moved.
   *
   * A trade is counted the moment it lands and taken back out when it ages
   * past the window, so what this adds stays honest on its own. It still
   * cannot know about the trades the server counted before anybody was
   * watching, and those age out too — which is what the rebuilt card is for.
   */
  const applyTradeAction = action(
    (
      ctx,
      trade: {
        isBuy: boolean
        volumeUsd: BigNumber
        priceUsd: BigNumber
        at?: number
      },
    ) => {
      if (tracksExpiry && windowMs !== undefined) {
        const now = trade.at ?? Date.now()
        counted.push({ at: now, isBuy: trade.isBuy, volumeUsd: trade.volumeUsd })
        while (counted.length > 0 && now - counted[0]!.at > windowMs) {
          drop(ctx, counted.shift()!)
        }
      }

      transactionsAtom(ctx, ctx.get(transactionsAtom) + 1)

      if (trade.isBuy) {
        buysAtom(ctx, ctx.get(buysAtom) + 1)
        buyVolumeUsdAtom.set(
          ctx,
          ctx.get(buyVolumeUsdAtom).plus(trade.volumeUsd),
        )
      } else {
        sellsAtom(ctx, ctx.get(sellsAtom) + 1)
        sellVolumeUsdAtom.set(
          ctx,
          ctx.get(sellVolumeUsdAtom).plus(trade.volumeUsd),
        )
      }

      volumeUsdAtom.set(ctx, ctx.get(volumeUsdAtom).plus(trade.volumeUsd))
      netVolumeInUsdAtom.set(
        ctx,
        ctx
          .get(netVolumeInUsdAtom)
          .plus(trade.isBuy ? trade.volumeUsd : trade.volumeUsd.negated()),
      )

      // The change over the window is the move from where the window opened,
      // so a fresh price gives it without waiting for the server to say so.
      const startPrice = ctx.get(startPriceAtom)
      if (startPrice.isGreaterThan(0)) {
        priceChangeAtom.set(
          ctx,
          trade.priceUsd.minus(startPrice).dividedBy(startPrice).times(100),
        )
      }
    },
    'applyTradeAction',
  )

  return {
    transactions: transactionsAtom,
    makers: makersAtom,
    buys: buysAtom,
    sells: sellsAtom,
    buyers: buyersAtom,
    sellers: sellersAtom,
    buyVolumeUsd: buyVolumeUsdAtom,
    sellVolumeUsd: sellVolumeUsdAtom,
    volumeUsd: volumeUsdAtom,
    startPrice: startPriceAtom,
    priceChange: priceChangeAtom,
    volumeChangePercents: volumeChangePercentsAtom,
    netVolumeInUsd: netVolumeInUsdAtom,
    holdersChange: holdersChangeAtom,

    updateFromDto: updateFromDtoAction,
    applyTrade: applyTradeAction,
  }
}
export type PairPeriodStatsModel = ReturnType<typeof reatomPairStatsForPeriod>

export const reatomPairPeriodStats = (dto: ServicesApi.PairDto) => {
  const stats = {
    '5M': reatomPairStatsForPeriod(dto, '5M'),
    '1H': reatomPairStatsForPeriod(dto, '1H'),
    '6H': reatomPairStatsForPeriod(dto, '6H'),
    '1D': reatomPairStatsForPeriod(dto, '1D'),
    ALL: reatomPairStatsForPeriod(dto, 'ALL'),
  }

  const updateFromDtoAction = action(
    (ctx, dto: ServicesApi.PairDto['periodStats']) => {
      stats['5M'].updateFromDto(ctx, dto)
      stats['1H'].updateFromDto(ctx, dto)
      stats['6H'].updateFromDto(ctx, dto)
      stats['1D'].updateFromDto(ctx, dto)
      stats['ALL'].updateFromDto(ctx, dto)
    },
  )

  /** A trade lands in every window at once; it just happened. */
  const applyTradeAction = action(
    (
      ctx,
      trade: {
        isBuy: boolean
        volumeUsd: BigNumber
        priceUsd: BigNumber
        at?: number
      },
    ) => {
      stats['5M'].applyTrade(ctx, trade)
      stats['1H'].applyTrade(ctx, trade)
      stats['6H'].applyTrade(ctx, trade)
      stats['1D'].applyTrade(ctx, trade)
      stats.ALL.applyTrade(ctx, trade)
    },
    'applyTradeAction',
  )

  const allTimeTxCountAtom = atom((ctx) => {
    const txs = ctx.spy(stats['ALL'].transactions)
    return txs
  })

  return {
    stats,
    updateFromDto: updateFromDtoAction,
    applyTrade: applyTradeAction,
    allTimeTxCount: allTimeTxCountAtom,
  }
}

type PairPeriodStatsMap = Record<PairMetrics.MetricsSpan, PairPeriodStatsModel>

export const map = <B>(
  pairPeriodStatsMap: PairPeriodStatsMap,
  fn: (pairPeriodStats: PairPeriodStatsModel) => B,
) => {
  return {
    ALL: fn(pairPeriodStatsMap['ALL']),
    '1D': fn(pairPeriodStatsMap['1D']),
    '6H': fn(pairPeriodStatsMap['6H']),
    '1H': fn(pairPeriodStatsMap['1H']),
    '5M': fn(pairPeriodStatsMap['5M']),
  }
}
